/**
 * useChat — chat state + SSE communication for the Pack Studio.
 *
 * Shared between the public pack-share SPA and the admin Pack Viewer.
 * The transport layer (URL prefix) is selected via `authConfig`:
 *
 *   { mode: 'public', token }                         → /api/v1/public/pack/{token}/...
 *   { mode: 'admin',  packId, clientUid, apiBase? }   → /api/v1/creative/packs/{packId}/...
 *
 * Auth posture matches the rest of the admin / creative-canvas surface
 * — no Authorization header. The admin endpoints are gateway-trusted.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface EditResult {
  edit_id: string;
  ad_brief_id: string;
  instruction: string;
  version?: number;
  before_url?: string | null;
  after_url?: string | null;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  error?: string;
  headline?: string | null;
  persona?: string | null;
}

export interface ChatMention {
  type: string;
  id: string;
  label: string;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  target_ads: string[];
  edit_results: EditResult[];
  mentions?: ChatMention[];
}

export type ChatAuthConfig =
  | { mode: 'public'; token: string; apiBase?: string }
  | { mode: 'admin'; packId: string; clientUid: string; apiBase?: string };

interface ChatState {
  sessionId: string | null;
  messages: ChatMessage[];
  activeAdIds: string[];
  isStreaming: boolean;
  error: string | null;
}

/**
 * Resolves the chat resource URL for a given sub-path. Returns a fully
 * qualified URL when `apiBase` is set, else a relative URL (Vite proxy
 * handles dev forwarding).
 */
function resolveChatUrl(cfg: ChatAuthConfig, suffix: string): string {
  const base = cfg.apiBase ? cfg.apiBase.replace(/\/$/, '') : '';
  if (cfg.mode === 'public') {
    return `${base}/api/v1/public/pack/${cfg.token}${suffix}`;
  }
  // Admin scopes by client_uid as a query param; preserve any caller-provided
  // query suffix by detecting an existing '?'.
  const sep = suffix.includes('?') ? '&' : '?';
  return `${base}/api/v1/creative/packs/${cfg.packId}${suffix}${sep}client_uid=${encodeURIComponent(cfg.clientUid)}`;
}

/**
 * Stable key for `useEffect`/`useCallback` deps so we don't re-fire on
 * shallow object identity churn from the parent.
 */
function authKey(cfg: ChatAuthConfig): string {
  if (cfg.mode === 'public') return `public:${cfg.token}:${cfg.apiBase ?? ''}`;
  return `admin:${cfg.clientUid}:${cfg.packId}:${cfg.apiBase ?? ''}`;
}

/**
 * Whether the auth config has enough information to make API calls. For
 * admin mode this lets callers mount the hook before pack data has loaded
 * (since hooks can't be called conditionally) — fetches/operations are no-
 * ops until both `packId` and `clientUid` are non-empty.
 */
function authReady(cfg: ChatAuthConfig): boolean {
  if (cfg.mode === 'public') return Boolean(cfg.token);
  return Boolean(cfg.packId) && Boolean(cfg.clientUid);
}

export function useChat(authConfig: ChatAuthConfig) {
  const [state, setState] = useState<ChatState>({
    sessionId: null,
    messages: [],
    activeAdIds: [],
    isStreaming: false,
    error: null,
  });

  const streamingEditsRef = useRef<Map<string, EditResult>>(new Map());
  const abortRef = useRef<AbortController | null>(null);

  const cfgKey = authKey(authConfig);
  const ready = authReady(authConfig);
  // Memoise the config object on its content so child callbacks have stable deps.
  const cfg = useMemo(() => authConfig, [cfgKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load session on mount (and when the auth config materially changes).
  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    fetch(resolveChatUrl(cfg, '/chat/session'))
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          sessionId: data.session_id,
          messages: data.messages || [],
          activeAdIds: data.active_ad_ids || [],
          error: null,
        }));
      })
      .catch((e: Error) => {
        if (cancelled) return;
        console.warn('Failed to load chat session:', e);
        setState((prev) => ({ ...prev, error: e.message }));
      });
    return () => {
      cancelled = true;
    };
  }, [cfg, ready]);

  const toggleAd = useCallback(
    async (adBriefId: string) => {
      if (!ready) return;
      const isActive = state.activeAdIds.includes(adBriefId);
      const newIds = isActive
        ? state.activeAdIds.filter((id) => id !== adBriefId)
        : [...state.activeAdIds, adBriefId];

      setState((prev) => ({ ...prev, activeAdIds: newIds }));

      try {
        await fetch(resolveChatUrl(cfg, '/chat/toggle-ad'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ad_brief_id: adBriefId,
            action: isActive ? 'remove' : 'add',
          }),
        });
      } catch {
        setState((prev) => ({ ...prev, activeAdIds: state.activeAdIds }));
      }
    },
    [cfg, ready, state.activeAdIds],
  );

  const selectAllAds = useCallback(
    (allAdIds: string[]) => {
      if (!ready) return;
      setState((prev) => ({ ...prev, activeAdIds: allAdIds }));
      for (const id of allAdIds) {
        void fetch(resolveChatUrl(cfg, '/chat/toggle-ad'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ad_brief_id: id, action: 'add' }),
        });
      }
    },
    [cfg, ready],
  );

  const clearSelection = useCallback(() => {
    if (!ready) return;
    const oldIds = state.activeAdIds;
    setState((prev) => ({ ...prev, activeAdIds: [] }));
    for (const id of oldIds) {
      void fetch(resolveChatUrl(cfg, '/chat/toggle-ad'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ad_brief_id: id, action: 'remove' }),
      });
    }
  }, [cfg, ready, state.activeAdIds]);

  const sendMessage = useCallback(
    async (
      content: string,
      onImageUpdate?: (adBriefId: string, newUrl: string) => void,
      overrideTargetAdIds?: string[],
      mentions?: ChatMention[],
    ) => {
      if (!ready) return;
      if (!content.trim() || state.isStreaming) return;

      const targetAds = overrideTargetAdIds ?? state.activeAdIds;

      const userMsg: ChatMessage = {
        id: `msg_${Date.now()}`,
        role: 'user',
        content,
        timestamp: new Date().toISOString(),
        target_ads: targetAds,
        edit_results: [],
        mentions,
      };

      setState((prev) => ({
        ...prev,
        messages: [...prev.messages, userMsg],
        isStreaming: true,
        error: null,
      }));

      const assistantMsg: ChatMessage = {
        id: `msg_${Date.now()}_asst`,
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
        target_ads: [],
        edit_results: [],
      };

      setState((prev) => ({
        ...prev,
        messages: [...prev.messages, assistantMsg],
      }));

      streamingEditsRef.current.clear();

      try {
        abortRef.current = new AbortController();
        const response = await fetch(resolveChatUrl(cfg, '/chat'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            content,
            target_ads: targetAds,
            mentions: mentions ?? [],
          }),
          signal: abortRef.current.signal,
        });

        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error((err as { detail?: string }).detail || `HTTP ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // SSE format: "event: <type>\ndata: <json>\n\n"
          const events = buffer.split('\n\n');
          buffer = events.pop() || '';

          for (const rawEvent of events) {
            if (!rawEvent.trim()) continue;
            const lines = rawEvent.split('\n');
            const eventLine = lines.find((l) => l.startsWith('event: '));
            const dataLine = lines.find((l) => l.startsWith('data: '));
            if (!dataLine) continue;
            const eventType = eventLine ? eventLine.slice(7).trim() : 'data';
            try {
              const data = JSON.parse(dataLine.slice(6));
              _processSSEEvent(eventType, data, assistantMsg, streamingEditsRef.current, setState, onImageUpdate);
            } catch {
              // Skip malformed JSON
            }
          }
        }
      } catch (e) {
        if ((e as Error).name !== 'AbortError') {
          setState((prev) => ({
            ...prev,
            error: (e as Error).message,
          }));
        }
      } finally {
        setState((prev) => ({ ...prev, isStreaming: false }));
        abortRef.current = null;
      }
    },
    [cfg, ready, state.activeAdIds, state.isStreaming],
  );

  const cancelStream = useCallback(() => {
    abortRef.current?.abort();
    setState((prev) => ({ ...prev, isStreaming: false }));
  }, []);

  const retrySession = useCallback(() => {
    if (!ready) return;
    setState((prev) => ({ ...prev, error: null }));
    fetch(resolveChatUrl(cfg, '/chat/session'))
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        setState((prev) => ({
          ...prev,
          sessionId: (data as { session_id: string }).session_id,
          messages: (data as { messages?: ChatMessage[] }).messages || [],
          activeAdIds: (data as { active_ad_ids?: string[] }).active_ad_ids || [],
          error: null,
        }));
      })
      .catch((e: Error) => {
        console.warn('Failed to retry chat session:', e);
        setState((prev) => ({ ...prev, error: e.message }));
      });
  }, [cfg, ready]);

  return {
    ...state,
    toggleAd,
    selectAllAds,
    clearSelection,
    sendMessage,
    cancelStream,
    retrySession,
  };
}

/**
 * Fetches the @-reference dropdown items for the chat composer. Tolerates
 * either the legacy array shape or the new `{ ads, personas, attachments }`
 * shape — flattens both to a single list.
 */
export function useMentionables(authConfig: ChatAuthConfig): MentionableItem[] {
  const [items, setItems] = useState<MentionableItem[]>([]);
  const cfgKey = authKey(authConfig);
  const ready = authReady(authConfig);

  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    fetch(resolveChatUrl(authConfig, '/mentionables'))
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((resp: MentionablesResponse | MentionableItem[]) => {
        if (cancelled) return;
        const flat: MentionableItem[] = Array.isArray(resp)
          ? resp
          : [
              ...(resp.ads ?? []),
              ...(resp.personas ?? []),
              ...(resp.attachments ?? []),
            ];
        setItems(flat);
      })
      .catch((e: Error) => {
        if (!cancelled) console.warn('Failed to load mentionables:', e);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfgKey, ready]);

  return items;
}

export interface MentionableItem {
  id: string;
  type: 'ad' | 'persona' | 'attachment';
  label: string;
  sublabel?: string;
  thumbnailUrl?: string;
}

interface MentionablesResponse {
  ads?: MentionableItem[];
  personas?: MentionableItem[];
  attachments?: MentionableItem[];
}

type ChatStateSetter = React.Dispatch<React.SetStateAction<ChatState>>;

function _processSSEEvent(
  eventType: string,
  data: Record<string, unknown>,
  assistantMsg: ChatMessage,
  edits: Map<string, EditResult>,
  setState: ChatStateSetter,
  onImageUpdate?: (adBriefId: string, newUrl: string) => void,
) {
  if (eventType === 'message') {
    assistantMsg.content = (data.content as string) || '';
    _flushAssistant(assistantMsg, edits, setState);
    return;
  }

  if (eventType === 'done') {
    _flushAssistant(assistantMsg, edits, setState);
    return;
  }

  const editId = data.edit_id as string | undefined;
  const adBriefId = data.ad_brief_id as string | undefined;
  if (!editId || !adBriefId) return;

  if (eventType === 'edit_start') {
    edits.set(editId, {
      edit_id: editId,
      ad_brief_id: adBriefId,
      instruction: (data.instruction as string) || '',
      status: 'pending',
      headline: (data.headline as string | null) || null,
      persona: (data.persona as string | null) || null,
    });
  } else if (eventType === 'edit_progress') {
    const existing = edits.get(editId) || {
      edit_id: editId,
      ad_brief_id: adBriefId,
      instruction: '',
      status: 'pending' as const,
    };
    edits.set(editId, { ...existing, status: 'processing' });
  } else if (eventType === 'edit_complete') {
    const existing = edits.get(editId) || {
      edit_id: editId,
      ad_brief_id: adBriefId,
      instruction: (data.instruction as string) || '',
      status: 'pending' as const,
    };
    const afterUrl = (data.after_url as string | null) || null;
    edits.set(editId, {
      ...existing,
      status: 'completed',
      version: data.version as number,
      after_url: afterUrl,
      headline: (data.headline as string | null) || existing.headline || null,
    });
    if (afterUrl && onImageUpdate) {
      onImageUpdate(adBriefId, afterUrl);
    }
  } else if (eventType === 'edit_error') {
    const existing = edits.get(editId) || {
      edit_id: editId,
      ad_brief_id: adBriefId,
      instruction: '',
      status: 'pending' as const,
    };
    edits.set(editId, { ...existing, status: 'failed', error: (data.error as string) || 'Unknown error' });
  }

  _flushAssistant(assistantMsg, edits, setState);
}

function _flushAssistant(
  assistantMsg: ChatMessage,
  edits: Map<string, EditResult>,
  setState: ChatStateSetter,
) {
  setState((prev) => {
    const msgs = [...prev.messages];
    const lastIdx = msgs.length - 1;
    if (lastIdx >= 0 && msgs[lastIdx].role === 'assistant') {
      msgs[lastIdx] = { ...assistantMsg, edit_results: Array.from(edits.values()) };
    }
    return { ...prev, messages: msgs };
  });
}
